import { getApiUrl } from '../config/api';
import { getApiErrorMessage, readJsonSafe } from './apiResponseService';

interface AuthMessageResponse {
    message: string;
}

interface ApiErrorPayload {
    message?: string;
    error?: string;
    statusCode?: number;
}

export const forgotPassword = async (email: string): Promise<AuthMessageResponse> => {
    const response = await fetch(getApiUrl('auth/forgot-password'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
    });

    const data = await readJsonSafe<AuthMessageResponse & ApiErrorPayload>(response);

    if (!response.ok) {
        throw new Error(getApiErrorMessage(response, 'Failed to request password reset.', data));
    }

    if (!data?.message) {
        return { message: 'If the email exists, password reset instructions have been sent.' };
    }

    return { message: data.message };
};

export const resetPassword = async (token: string, newPassword: string): Promise<AuthMessageResponse> => {
    const response = await fetch(getApiUrl('auth/reset-password'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, newPassword })
    });

    const data = await readJsonSafe<AuthMessageResponse & ApiErrorPayload>(response);

    if (!response.ok) {
        throw new Error(getApiErrorMessage(response, 'Failed to reset password.', data));
    }

    if (!data?.message) {
        return { message: 'Password reset successfully' };
    }

    return { message: data.message };
};

export const verifyEmail = async (token: string): Promise<AuthMessageResponse> => {
    const response = await fetch(getApiUrl('auth/verify-email'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
    });

    const data = await readJsonSafe<AuthMessageResponse & ApiErrorPayload>(response);

    if (!response.ok) {
        throw new Error(getApiErrorMessage(response, 'Failed to verify email.', data));
    }

    if (!data?.message) {
        return { message: 'Email verified successfully' };
    }

    return { message: data.message };
};

export const resendProfileEmailVerification = async (token: string): Promise<AuthMessageResponse> => {
    const response = await fetch(getApiUrl('users/profile/email-verification/resend'), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    const data = await readJsonSafe<AuthMessageResponse & ApiErrorPayload>(response);

    if (!response.ok) {
        throw new Error(getApiErrorMessage(response, 'Failed to resend verification email.', data));
    }

    if (!data?.message) {
        return { message: 'Verification email sent successfully' };
    }

    return { message: data.message };
};
